/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2021 ICEMEDX, Inc.
***********************************************/
import { Router } from "@angular/router";
import { Validators } from "@angular/forms";
import { BaseListView } from "./baseview.component";

import { LoginService } from "../login/login.service";
import { IceNavService } from "./ice-nav/ice-nav.service";
import { DomainAppService } from "../services/baseapp.service";
import { DomainService } from "../services/domain/domain.service";
import { MessageService } from "../services/message/message.service";
import { MedicalcodesService } from "../services/medicalcodes/medicalcodes.service";

import { IcecontrolSettings, iceColumnDefinition } from "./models/icesettings";
import { userTypes } from "../login/login.config";
import { Iceutility } from "../services/iceutility";
import { IceTypes } from "../models/icetypes";

import { IIceEntity } from "../models/core/IceEntity";
import { externaldata } from "../models/ehr/externaldata";
import { basecode } from "../models/ehr/subs/basecode";

export class BaseDocumentsComponent extends BaseListView {
  private defaultApi: string = "";
  newDoc: externaldata = null;
  docTitleCtrl: IcecontrolSettings = null;
  docTypeCtrl: IcecontrolSettings = new IcecontrolSettings("iceselect", { menuItems: [], width: "475px" }, null, null, "sub_type", "Select Type")
    .setControlName("sub_type")
    .createFormControl("", [Validators.required]);

  docStatusCtrl: IcecontrolSettings = new IcecontrolSettings("iceselect", { menuItems: [], width: "475px" }, null, null, "note_status_type", "Select Status")
    .setControlName("note_status_type")
    .createFormControl("", [Validators.required])
    .setOptions(DomainService.getNoteStatuses());

  docDateCtrl: IcecontrolSettings = null;
  docFileCtrl: IcecontrolSettings = null;

  constructor(appSvc: DomainAppService,
    loginSvc: LoginService,
    private messageSvc: MessageService,
    navSvc: IceNavService,
    private domSvc: DomainService,
    private medCodeSvc: MedicalcodesService,
    router: Router,
    defApi: string) {

    super(appSvc, loginSvc, navSvc, router);
    this.defaultApi = defApi;

    let feaAccess = this.getPermission(userTypes.USER_FEATURES.ufExternalDocuments).default_access;
    this.nameOfView = "Documents";
    appSvc.defaultMenus();
    appSvc.addBreadcrumb("List of " + this.nameOfView, {}, true, "");

    if (feaAccess == userTypes.USER_FEATURE_ACCESS.ufaNone) {
      setTimeout(function () {
        messageSvc.logMessage(IceTypes.LOGTYPE.ltError, "You do not have access to view this data.");
      });
    } else {
      this.getAppService().setCodeList(this.medCodeSvc, new basecode("SUBTYPE_ITEM"), this.docTypeCtrl,
        [
          IceTypes.SourceType.srcPayment.toString()]);

      this.setInlineEditing(true);
      this.dataViewSettings = this.viewSetup([], userTypes.USER_FEATURES.ufExternalDocuments);

      domSvc.getDocuments(this.defaultApi, appSvc.getCurrentAccount(), appSvc.getCurrentAccountType(), IceTypes.SourceType.srcUndefined).subscribe(
        data => {
          if (data && data.length > 0) {
            for (let i = 0; i < data.length; i++) {
              let eobj: externaldata = <externaldata>data[0];
              if (eobj == null) continue;

              eobj.external_reference_date = Iceutility.getDateTextEx(Iceutility.ConvertFromDotNetServerTime(eobj.external_reference_date));
              eobj.external_reference_end_date = Iceutility.getDateTextEx(Iceutility.ConvertFromDotNetServerTime(eobj.external_reference_end_date));
              let foundType = this.docTypeCtrl.getOptions().filter(t => t.value == eobj.sub_type);
              if (foundType.length > 0)
                eobj.sub_type = foundType[0].name;

              let foundStatus = DomainService.getNoteStatuses().filter(s => s.value == eobj.note_status_type);
              if (foundStatus.length > 0)
                eobj.note_status_type = foundStatus[0].name;

              eobj.modified = Iceutility.getDateTextEx(Iceutility.ConvertFromDotNetServerTime(eobj.modified));
            }
            this.refreshTableData(data);
          }
          else {
            this.refreshTableData([]);
          }
        },
        error => {
        });
    }
  }

  getDefaultApi(): string {
    return this.defaultApi;
  }

  onGetColumnDefinition(): iceColumnDefinition[] {
    return [
      new iceColumnDefinition("title", "Document", false, false,
        new IcecontrolSettings("icelabel", { width: "75px" }, null, null)),
      new iceColumnDefinition("original_path", "Name of File", false, false,
        new IcecontrolSettings("icelabel", { width: "75px" }, null, null)),
      new iceColumnDefinition("external_reference_date", "Date of Document", false, false,
        new IcecontrolSettings("icelabel", { width: "75px" }, null, null)),
      new iceColumnDefinition("sub_type", "Type", false, false,
        new IcecontrolSettings("icelabel", { width: "75px" }, null, null)),
      new iceColumnDefinition("note_status_type", "Status", false, false,
        new IcecontrolSettings("icelabel", { width: "75px" }, null, null)),
      new iceColumnDefinition("modified_user", "Mod. By", false, false,
        new IcecontrolSettings("icelabel", { width: "75px" }, null, null)),
      new iceColumnDefinition("modified", "Mod. Date", false, false,
        new IcecontrolSettings("icelabel", { width: "75px" }, null, null)),
      new iceColumnDefinition("QuickActions", "", false, false),
      new iceColumnDefinition("file", "Upload", false, true),
      new iceColumnDefinition("external_data_key", "external_data_key", true),
    ];
  }

  onGetActionItems(): any[] {
    return [IceTypes.recordQuickActionEdit, IceTypes.recordQuickActionView, IceTypes.recordQuickActionRemove];
  }

  onGetData(originalData: IIceEntity[]): any[] {
    return originalData;
  }

  onGetEditingControls(): IcecontrolSettings[] {
    /// Item New Panel
    this.docTitleCtrl = new IcecontrolSettings("iceinput", { width: "475px" }, null, null, "title", "Enter Name of Item")
      .setControlName("title")
      .createFormControl("", [Validators.required]);

    let dt = Iceutility.GetLocalDate();
    this.docDateCtrl = new IcecontrolSettings("icedatepicker", { placeholder: "Date", value: "", timevalue: "" }, null, null, "external_reference_date")
      .setControlName("external_reference_date")
      .createFormControl(Iceutility.getDateTextEx(dt), [Validators.required]);

    this.docFileCtrl = new IcecontrolSettings("icesingleupload", { width: "475px" }, null, null, "file", "Choose File")
      .setControlName("file")
      .createFormControl(null, [Validators.required]);

    return [
      this.docTitleCtrl,
      this.docDateCtrl,
      this.docTypeCtrl,
      this.docStatusCtrl,
      this.docFileCtrl
    ];
  }
  isRowSelectionEnabled(): boolean {
    return true;
  }

  /////////////////////////////////////
  // Events
  onButtonClick(event) {
    if (event.buttonSettings != null) {
      switch (event.buttonSettings.id) {
        default: {
          // Add New Item to DB
          let appSvc = this.getAppService();
          if (appSvc.processEditPanelSectionButtons(this.getEditingSectionTag(), event.buttonSettings.id)) {
            if (this.newDoc == null) break;

            let docDateandTime = Iceutility.GetDateText(this.docDateCtrl.getFormControl().value, null, true);

            // set times and duration
            this.newDoc.external_reference_date = Iceutility.ConvertToUTC(docDateandTime, true);
            this.newDoc.external_reference_end_date = this.newDoc.external_reference_date;

            // sub_type
            let foundType = this.docTypeCtrl.getOptions().filter(t => t.value == this.docTypeCtrl.getFormControl().value);
            if (foundType.length > 0) {
              this.newDoc.sub_type = foundType[0].value;
            }

            // status
            let foundStatus = this.docStatusCtrl.getOptions().filter(s => s.value == this.docStatusCtrl.getFormControl().value);
            if (foundStatus.length > 0) {
              this.newDoc.note_status_type = foundStatus[0].value;
            }

            let file: File = <File>this.docFileCtrl.getFormControl().value;
            if (file != null) {
              this.newDoc["file"] = file;
              this.newDoc.original_path = file.name;
            }

            if (this.newDoc.external_data_key.length == 0) {
              this.domSvc.insertDocument(this.defaultApi, appSvc.getCurrentAccount(), this.newDoc).subscribe(
                data => {
                  if (data == null || data.length == 0) return;
                  if (this.newDoc == null) return;

                  if (this.dataSource.data == null) this.dataSource.data = [];

                  let svrDoc: externaldata = <externaldata>data[0];
                  if (svrDoc == null) return;

                  this.newDoc.external_data_key = svrDoc.external_data_key;
                  this.dataSource.data.push(this.newDoc)
                  this.dataSource.data = this.dataSource.data;

                  if (file != null) {
                    this.domSvc.insertFile(this.defaultApi, appSvc.getCurrentAccount(), this.newDoc.external_data_key, this.newDoc.parent_src_type, file).subscribe(
                      data => {
                        if (data == null || data.length == 0) return;
                        if (this.newDoc == null) return;

                        // Reset;
                        this.newDoc = null;
                      },
                      error => {
                        // To do, log error
                      });
                  }
                  else {
                    this.newDoc = null;
                  }
                },
                error => {
                  // To do, log error
                });
            }
            else {
              this.domSvc.updateDocument(this.defaultApi, appSvc.getCurrentAccount(), this.newDoc).subscribe(
                data => {
                  if (data == null || data.length == 0) return;
                  if (this.newDoc == null) return;

                  if (this.dataSource.data == null) this.dataSource.data = [];

                  let svrDoc: externaldata = <externaldata>data[0];
                  if (svrDoc == null) return;

                  this.newDoc.external_data_key = svrDoc.external_data_key;
                  this.dataSource.data = this.dataSource.data;

                  if (file != null) {
                    this.domSvc.updateFile(this.defaultApi, appSvc.getCurrentAccount(), this.newDoc.external_data_key, this.newDoc.parent_src_type, file).subscribe(
                      data => {
                        if (data == null || data.length == 0) return;
                        if (this.newDoc == null) return;

                        // Reset;
                        this.newDoc = null;
                      },
                      error => {
                        // To do, log error
                      });
                  }
                  else {
                    this.newDoc = null;
                  }
                },
                error => {
                  // To do, log error
                });
            }
          }
          break;
        }
      }
    }
  }

  onControlChange(event) {
    if (event.singleUploadSettings != null) {
      switch (event.singleUploadSettings.id) {
        case "file": {
          break;
        }
      }
    }
  }

  onAddRowButtonClick(event) {
    if (event.matTableSettings != null) {
      switch (event.matTableSettings.tag) {
        default: {
          this.getAppService().showEditPanelSection(this.getEditingSectionTag());
          this.newDoc = null; // reset
          this.newDoc = new externaldata();
          this.newDoc.parent_src_type = IceTypes.SourceType.srcMiscellaneousForms;
          this.newDoc.src_type = IceTypes.SourceType.srcMiscellaneousForms;
          this.getAppService().editPanelSectionUpdate(this.getEditingSectionTag(), false, this.newDoc);
          break;
        }
      }
    }
  }

  onMatGridRowActionClick(event) {
    if (event["rowData"] == null) {
      // To do log error
      return;
    }

    if (event.actionItem == null) return;
    if (IceTypes.recordQuickActionEdit.value == event.actionItem.value) {
      let doc = <externaldata>event["rowData"];
      if (doc == null) {
        // To do log error
        return;
      }

      this.getAppService().showEditPanelSection(this.getEditingSectionTag());
      this.newDoc = doc; // reset

      // To do remove: not needed anymore
      this.newDoc.external_reference_date = Iceutility.GetLocalDate(Iceutility.convertToUTCEx(this.newDoc.external_reference_date));
      this.newDoc.external_reference_end_date = Iceutility.GetLocalDate(Iceutility.convertToUTCEx(this.newDoc.external_reference_end_date));

      let foundType = this.docTypeCtrl.getOptions().filter(t => t.name == this.newDoc.external_data_type);
      if (foundType.length > 0) {
        this.docTypeCtrl.getFormControl().setValue(Iceutility.toLong(foundType[0].value));
      }
      else {
        this.docTypeCtrl.getOptions().push({ name: "None", value: 0 });
        this.docTypeCtrl.getFormControl().setValue(Iceutility.toLong(this.newDoc.sub_type));
      }

      let foundStatus = this.docStatusCtrl.getOptions().filter(t => t.name == this.newDoc.note_status_type);
      if (foundStatus.length > 0) {
        this.docStatusCtrl.getFormControl().setValue(Iceutility.toLong(foundStatus[0].value));
      }

      this.getAppService().editPanelSectionUpdate(this.getEditingSectionTag(), false, this.newDoc);
    }
    else if (IceTypes.recordQuickActionRemove.value == event.actionItem.value) {
      let doc = <externaldata>event["rowData"];
      if (doc == null) {
        // To do log error
        return;
      }
      this.domSvc.deleteDocument(this.defaultApi, this.getAppService().getCurrentAccount(), doc.external_data_key, doc.parent_src_type).subscribe(
        data => {
          if (this.dataSource.data != null && this.dataSource.data.length > 0) {
            this.dataSource.data = this.dataSource.data.filter(d => d.external_data_key != doc.external_data_key);
          }
        },
        error => {
          // To do, log error
        });
    }
    else if (IceTypes.recordQuickActionView.value == event.actionItem.value) {
      let doc = <externaldata>event["rowData"];
      if (doc == null) {
        // To do log error
        return;
      }
      this.domSvc.downloadDocument(this.defaultApi, this.getAppService().getCurrentAccount(), event.rowData.external_data_key, event.rowData.parent_src_type);
    }
  }

  onMatGridRowClick(event) {
    if (event.rowData == null) {
      this.messageSvc.logMessage(IceTypes.LOGTYPE.ltError, "An error occurred while downloading the document.");
      return;
    }

    // fall through, for now
  }
}
